import styled from '@emotion/styled';
import { Button, ButtonGroup, pxToRem } from '@modernhealth/vital-components';
import { captureException } from '@sentry/browser';
import { Input } from 'antd';
import range from 'lodash/range';
import React, { useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';

import { appClient } from 'src/config/apollo';
import { color, font } from 'src/shared/styles/variables';

import { useSubmitNpsMutation } from '../documents/submitNps.generated';

const HeaderText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 40,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const RatingWrapper = styled.div({
    position: 'relative',
});

const RatingList = styled.ul({
    display: 'flex',
    padding: 0,
    marginBottom: 26,
});

interface RatingElementProps {
    active: boolean;
}

const RatingElement = styled.li<RatingElementProps>(props => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 6.5px',
    width: 62,
    height: 62,
    borderRadius: 31,
    border: `1.8px solid ${color.catskillWhite}`,
    boxShadow: '0px 5.2px 15.6px rgba(218, 225, 234, 0.5)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    backgroundColor: props.active ? color.royalBlue : color.white,
}));

interface RatingElementTextProps {
    active: boolean;
}

const RatingElementText = styled.span<RatingElementTextProps>(props => ({
    marginTop: 3,
    color: props.active ? color.white : color.blueBayoux,
    transition: 'color 0.3s',
    userSelect: 'none',
    ...font.heavy(font.size.mediumExtended),
}));

const LabelsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 6.5px',
});

const LabelText = styled.span({
    color: color.blueBayoux,
    ...font.heavy(15),
});

const SkipButtonWrapper = styled.div({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: -140,
});

const TextArea = styled(Input.TextArea)({
    '&&': {
        width: '100%',
        marginTop: 40,
        padding: 11,
        border: `2px solid ${color.casper}`,
        borderRadius: 5,
        resize: 'none',
        color: color.blueBayoux,
        ...font.medium(font.size.base),
    },
});

const ButtonGroupWrapper = styled.div({
    marginTop: pxToRem(35),
});

interface Props {
    onSkip: () => void;
    onSubmit: () => void;
    sessionUuid: string;
}

export const RateSessionNPSQuestionStep: React.FC<Props> = ({ onSkip, onSubmit, sessionUuid }) => {
    const { t } = useTranslation();

    const [rating, setRating] = useState<null | number>(null);
    const [feedbackText, setFeedbackText] = useState('');

    const [submitNps, { loading }] = useSubmitNpsMutation({
        client: appClient,
    });

    const handleSubmit = async () => {
        if (rating === null || loading) {
            return;
        }

        try {
            const { data } = await submitNps({
                variables: {
                    coachingVisitUuid: sessionUuid,
                    rating,
                    feedbackText,
                },
            });

            const ok = data?.submitNps?.ok;

            if (!ok) {
                throw new Error('SubmitNPSMutation: received non-ok response.');
            }
        } catch (error) {
            captureException(error);
        }

        onSubmit();
    };

    return (
        <React.Fragment>
            <HeaderText>{t('videoSessions:rate_session.nps_question.title')}</HeaderText>
            <RatingWrapper>
                <RatingList>
                    {range(0, 11).map(val => (
                        // eslint-disable-next-line react/jsx-key
                        <RatingElement onClick={() => setRating(val)} active={rating === val}>
                            <RatingElementText active={rating === val}>{val}</RatingElementText>
                        </RatingElement>
                    ))}
                </RatingList>
                <LabelsWrapper>
                    <LabelText>
                        {t('videoSessions:rate_session.nps_question.range_label.min')}
                    </LabelText>
                    <LabelText>
                        {t('videoSessions:rate_session.nps_question.range_label.max')}
                    </LabelText>
                </LabelsWrapper>

                {rating === null ? (
                    <SkipButtonWrapper>
                        <Button onClick={onSkip} variant="flat" fluid>
                            {t('common.skip')}
                        </Button>
                    </SkipButtonWrapper>
                ) : null}
            </RatingWrapper>
            <AnimateHeight
                duration={300}
                height={rating === null ? 0 : 'auto'}
                style={{
                    width: '100%',
                    opacity: rating === null ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out 0.25s',
                }}
            >
                <TextArea
                    rows={10}
                    placeholder={t('videoSessions:rate_session.nps_question.text_area.placeholder')}
                    value={feedbackText}
                    onChange={({ target }) => setFeedbackText(target.value)}
                />

                <ButtonGroupWrapper>
                    <ButtonGroup>
                        <Button onClick={onSkip} variant="secondary">
                            {t('common.skip')}
                        </Button>
                        <Button onClick={handleSubmit} busy={loading} variant="primary">
                            {t('common.submit')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupWrapper>
            </AnimateHeight>
        </React.Fragment>
    );
};
